import type { AutocompleteOption, ParsedAddress, PlaceDetails } from "./google-places";
import { ADDRESS_COMPONENT_TYPE_MAPPING, PLACES_API_CONFIG } from "./google-places-constants";
import type { GooglePlacePrediction } from "./use-places-api";

const COUNTRIES_WITH_STATE_ABBREVIATIONS = new Set(["US", "CA", "AU", "MX", "BR"]);

// Checks whether an address contains a state abbreviation as a whole word in the combined main and secondary text.
// Example: matchesStateFilter("123 Main St", "Austin, TX, USA", ["TX", "CA"]) => true
//          matchesStateFilter("123 Main St", "Austin, TX, USA", ["CA"])       => false
const matchesStateFilter = (mainText: string, secondaryText: string, states?: string[]): boolean => {
  if (!states?.length) {
    return true;
  }
  const upperText = `${mainText} ${secondaryText}`.toUpperCase();
  return states.some((state) => new RegExp(`\\b${state.toUpperCase()}\\b`).test(upperText));
};

export const formatAddressFromParts = (address: {
  streetNumber?: string;
  streetName?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}): string => {
  const addressParts = [
    address.streetNumber && address.streetName ? `${address.streetNumber} ${address.streetName}` : address.streetName,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ].filter(Boolean);

  return addressParts.join(", ");
};

export const createAddressParser = () => {
  return (addressComponents: PlaceDetails["addressComponents"]): ParsedAddress => {
    if (!addressComponents?.length) {
      return { formattedAddress: "" };
    }

    const parsed: ParsedAddress = { formattedAddress: "" };
    const fieldPrecedence: Record<string, number> = {};

    addressComponents.forEach((component) => {
      const matchingType = component.types.find((type) => type in ADDRESS_COMPONENT_TYPE_MAPPING);

      if (matchingType) {
        const mapping = ADDRESS_COMPONENT_TYPE_MAPPING[matchingType as keyof typeof ADDRESS_COMPONENT_TYPE_MAPPING];
        let textValue: string;
        if (mapping.field === "state" && "useShortText" in mapping && mapping.useShortText) {
          const currentCountry = addressComponents.find((c) => c.types.includes("country"))?.shortText ?? "";
          if (COUNTRIES_WITH_STATE_ABBREVIATIONS.has(currentCountry)) {
            textValue = component.shortText;
          } else {
            textValue = component.longText;
          }
        } else {
          textValue = "useShortText" in mapping && mapping.useShortText ? component.shortText : component.longText;
        }

        const currentPrecedence = fieldPrecedence[mapping.field] ?? Infinity;

        if (mapping.precedence < currentPrecedence) {
          parsed[mapping.field] = textValue;
          fieldPrecedence[mapping.field] = mapping.precedence;
        }
      }
    });

    // hacky fallback for now: we will extract city from state if city is missing
    if (!parsed.city && parsed.state) {
      // handling city name extraction from state - particularily for IE where we get County in the state name for some reason
      const countyMatch = parsed.state.match(/^County\s+(.+)$/i);
      if (countyMatch) {
        parsed.city = countyMatch[1];
      }
    }

    parsed.formattedAddress = formatAddressFromParts(parsed);
    return parsed;
  };
};

const createStreetAddressFallback = (
  placeDetails: PlaceDetails,
  city?: string,
  state?: string,
  mainText?: string
): string | undefined => {
  try {
    const addressParts = placeDetails.formattedAddress.split(", ");
    const firstPart = addressParts[0];

    if (!firstPart || firstPart.length < 2) {
      if (mainText && mainText.trim()) {
        return mainText.trim();
      }
      return undefined;
    }

    const isLikelyCityState =
      /^[A-Z]{2}$/.test(firstPart) ||
      addressParts.length <= 2 ||
      firstPart === city ||
      firstPart === state ||
      /^\d+$/.test(firstPart);

    if (isLikelyCityState) {
      if (mainText && mainText.trim() && mainText !== city && mainText !== state) {
        return mainText.trim();
      }
      return undefined;
    }

    if (firstPart.length > PLACES_API_CONFIG.addressSplitThreshold) {
      const words = firstPart.split(" ");
      if (words.length > 3) {
        const midPoint = Math.ceil(words.length / 2);
        return words.slice(0, midPoint).join(" ");
      }
    }

    return firstPart.length <= PLACES_API_CONFIG.maxAddressLength ? firstPart : undefined;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to create street address fallback:", error);
    return undefined;
  }
};

export const createAddressFieldSetters = <T>(
  form: {
    setValue: (name: keyof T, value: string, options?: { shouldValidate?: boolean; shouldDirty?: boolean }) => void;
  },
  fieldMapping: {
    streetAddress?: keyof T;
    city?: keyof T;
    state?: keyof T;
    postalCode?: keyof T;
    country?: keyof T;
    formattedAddress?: keyof T;
  }
) => {
  const clearAddressFields = (): void => {
    Object.values(fieldMapping).forEach((formField) => {
      if (formField) {
        form.setValue(formField, "");
      }
    });
  };

  const setAddressFields = (address: ParsedAddress, placeDetails: PlaceDetails, mainText?: string): void => {
    const { streetNumber, streetName, city, state, postalCode, country } = address;

    let streetAddress = [streetNumber, streetName].filter(Boolean).join(" ") || undefined;
    if (!streetAddress) {
      streetAddress = createStreetAddressFallback(placeDetails, city, state, mainText);
    }

    const fieldValues: Record<keyof typeof fieldMapping, string | undefined> = {
      streetAddress,
      city,
      state,
      postalCode,
      country,
      formattedAddress: placeDetails.formattedAddress,
    };

    clearAddressFields();

    Object.entries(fieldMapping).forEach(([field, formField]) => {
      const value = fieldValues[field as keyof typeof fieldValues];
      if (formField && value?.trim()) {
        form.setValue(formField, value.trim(), { shouldValidate: true, shouldDirty: true });
      }
    });
  };

  return Object.assign(setAddressFields, { clear: clearAddressFields });
};

export const processAutocompleteOptions = (
  predictions: Array<{ placePrediction: GooglePlacePrediction }>,
  states?: string[]
): AutocompleteOption[] => {
  const createOption = (prediction: GooglePlacePrediction): AutocompleteOption => {
    const fullText = prediction.text.text;

    return {
      id: prediction.placeId,
      text: fullText,
      label: fullText,
      value: prediction.placeId,
      subText: prediction.structuredFormat?.secondaryText?.text || "",
      prediction: {
        place_id: prediction.placeId,
        description: { text: fullText },
        structured_formatting: {
          main_text: { text: fullText },
          secondary_text: { text: prediction.structuredFormat?.secondaryText?.text || "" },
        },
      },
    };
  };

  let options = predictions.map(({ placePrediction }) => createOption(placePrediction));

  if (states?.length) {
    options = options.filter((option) => matchesStateFilter(option.text || "", option.subText || "", states));
  }

  return options;
};
